import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Modal, Form, Input, InputNumber, Select, Checkbox, message } from 'antd';
import CustomEditor from '../custom-editor';
import { useModalities } from '../../hooks/useModalities';
import './user-list.css';
import WorkLocationField from '../components/WorkLocationField';
import PrimaryCenterField from '../components/PrimaryCenterField';
import { getVisiblePermissions } from '../../utils/permissionVisibility';
import { getUserDetails } from '../../utils/helper';

// Max characters allowed in a username, configurable at runtime via env-config.js.
// 0 (or unset) means no limit.
const MAX_USERNAME_LENGTH = Number((window as any)?.ENV_CONFIG?.MAX_USERNAME_LENGTH) || 0;

// maxLength silently swallows further typing, so warn once (keyed toast) when the
// user keeps typing at the cap instead of leaving them wondering why nothing appears.
const warnOnUsernameLengthExceeded = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (!MAX_USERNAME_LENGTH) return;
  const target = e.currentTarget;
  if (target.value.length < MAX_USERNAME_LENGTH) return;
  if (target.selectionStart !== target.selectionEnd) return;
  if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    message.warning({
      content: `Only ${MAX_USERNAME_LENGTH} characters allowed`,
      key: 'username_length_warning',
    });
  }
};

const { Option } = Select;

const EditUser = ({
  visible,
  onCancel,
  onSubmit,
  userData,
  unitsList = [],
  rolesList = [],
  permissionsList = [],
}) => {
  const [form] = Form.useForm();
  const [formChanges, setFormChanges] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signatureHtml, setSignatureHtml] = useState(null);
  const [selectedRole, setSelectedRole] = useState<string | undefined>(undefined);
  const { modalityOptions } = useModalities();

  useEffect(() => {
    if (visible && userData) {
      // Reset form changes tracking when modal opens
      setFormChanges({});
      setIsSubmitting(false);

      // Set signature HTML from user data
      setSignatureHtml(userData.user_signature || '');
      setSelectedRole(userData.user_type);

      form.setFieldsValue({
        user_firstname: userData.user_firstname,
        user_lastname: userData.user_lastname,
        username: userData.username,
        user_email: userData.user_email,
        user_mobile: userData.user_mobile,
        user_type: userData.user_type,
        user_unit_name: userData.user_unit_name ? userData.user_unit_name.split(",") : [],
        primary_center_code: userData.primary_center_code ?? undefined,
        user_designation: userData.user_designation,
        user_degree: userData.user_degree,
        user_report_limit: userData.user_report_limit ?? null,
        password: '',
        confirmPassword: '',
        permissions: userData.user_permissions?.map(p => p.up_key) || [],
        user_modalities: userData.user_modalities || [],
        um_modality: userData.um_modality || undefined,
        consultant: userData.consultant
      });
    } else if (!visible) {
      // Clean up when modal closes
      form.resetFields();
      setFormChanges({});
      setIsSubmitting(false);
      setSignatureHtml(null);
      setSelectedRole(undefined);
    }
  }, [visible, userData, form]);

  // Signature change handler with XSS protection
  const handleSignatureChange = useCallback((html) => {
    // Basic XSS prevention - remove script tags and event handlers
    const sanitizedHtml = html
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '')
      .replace(/javascript:/gi, '');

    setSignatureHtml(sanitizedHtml);
  }, []);

  const onFieldsChange = useCallback((changedFields, allFields) => {
    const changes = {};
    changedFields.forEach(field => {
      const fieldName = field.name[0];
      const fieldValue = field.value;
      if (userData && fieldValue !== userData[fieldName]) {
        changes[fieldName] = fieldValue;
      }
    });
    setFormChanges(prev => ({ ...prev, ...changes }));

    // Clear work location when consultant checkbox is checked; re-filter the
    // permission list when the role changes.
    changedFields.forEach(field => {
      if (field.name[0] === 'consultant' && field.value) {
        form.setFieldsValue({
          user_unit_name: []
        });
      }
      if (field.name[0] === 'user_type') {
        setSelectedRole(field.value);
        // Signature only applies to report-signing roles; clear it when moving
        // to a role that doesn't show the signature section.
        if (!['hod', 'radiologist'].includes(field.value)) {
          setSignatureHtml('');
        }
        // Primary center only applies to radiologist/typist/hod; drop any
        // stale value when switching to a role that doesn't show that field.
        if (!['radiologist', 'typist', 'hod'].includes(field.value)) {
          form.setFieldsValue({ primary_center_code: undefined });
        }
      }
    });
  }, [userData, form]);

  const handleOk = useCallback(async () => {
    if (!userData) {
      console.error('No user data available');
      return;
    }

    try {
      setIsSubmitting(true);

      // Validate all form fields first
      const values = await form.validateFields();

      // Compare current form values with original user data to detect changes
      const changedValues: Record<string, any> = {};

      let permissionsFromForm = [];

      // Check each form field against original data
      Object.keys(values).forEach(key => {
        const formValue = values[key];
        let originalValue = userData[key];

        // Special handling for permissions field
        if (key === 'permissions') {
          originalValue = userData.user_permissions?.map(p => p.up_key) || [];
         // Compare arrays
          const hasChanged = JSON.stringify(formValue?.sort()) !== JSON.stringify(originalValue?.sort());
          permissionsFromForm = formValue;
          if (hasChanged) {
            changedValues[key] = formValue;
          }
          return;
        }

        // Special handling for user modalities field
        if (key === 'user_modalities') {
          const originalModalities = Array.isArray(userData.user_modalities) ? userData.user_modalities : [];
          const selectedModalities = Array.isArray(formValue) ? formValue : [];
          const hasChanged = JSON.stringify([...selectedModalities].sort()) !== JSON.stringify([...originalModalities].sort());
          if (hasChanged) {
              changedValues.um_list = selectedModalities.join(',');
          }
          return;
        }

        // Assignment modality (technicians). Needs its own branch: the generic
        // comparison below skips empty values, so clearing the field back to
        // "any modality" would never reach the server.
        if (key === 'um_modality') {
          const original = userData.um_modality || null;
          const selected = values.user_type === 'technician' ? (formValue || null) : null;
          if (selected !== original) {
            changedValues.um_modality = selected;
          }
          return;
        }

        // Special handling for consultant checkbox field
        if (key === 'consultant') {
          const originalConsultant = userData.consultant;
          const selectedConsultant = Boolean(formValue);
          if (selectedConsultant !== originalConsultant) {
            changedValues.consultant = selectedConsultant;
          }
          return;
        }

        // Detect actual changes (handle undefined/null/empty string cases)
        if (formValue !== originalValue && formValue !== '') {
          // Special handling for password fields - only include if provided
          if (key === 'password' || key === 'confirmPassword') {
            if (formValue && formValue.trim() !== '') {
              changedValues[key] = formValue;
            }
          } else {
            changedValues[key] = formValue;
          }
        }
      });

      // Remove confirmPassword from submission data
      delete changedValues.confirmPassword;

      // Check if signature has changed
      const originalSignature = userData.user_signature || '';
      if (signatureHtml !== originalSignature) {
        changedValues.user_signature = signatureHtml;
      }

      // Only submit if there are actual changes
      if (Object.keys(changedValues).length === 0) {
        message.info('No changes detected');
        onCancel();
        return;
      }

      let hasUserPermissionsChanged = false;
      Object.keys(changedValues).forEach(key => {
        if ( key === 'permissions') {
          hasUserPermissionsChanged = true;
        }
      });

      let submitData = {
        ...changedValues,
        existing_user_id: userData.user_id,
        consultant: Boolean(values.consultant),
      };

      if (!hasUserPermissionsChanged) {
        submitData['permissions'] = permissionsFromForm;
      }

      await onSubmit(submitData);
    } catch (errorInfo) {
      if (errorInfo.errorFields) {
        message.error('Please fix the validation errors in the form');
      } else {
        console.warn('Validation failed:', errorInfo);
        message.error('Failed to update user. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [form, userData, onSubmit, onCancel, signatureHtml]);

  // Memoized options for better performance
  const roleOptions = useMemo(() =>
    rolesList?.map(role => ({
      label: role.role_label,
      value: role.role_code
    })) || [], [rolesList]
  );

  // const unitOptions = useMemo(() =>
  //   unitsList?.map(unit => ({
  //     label: unit.unit_label,
  //     value: unit.unit_code
  //   })) || [], [unitsList]
  // );

  // Codes the user already holds — kept visible even if outside their role so an
  // unrelated save never silently revokes them.
  const grantedPermissionCodes = useMemo(
    () => userData?.user_permissions?.map(p => p.up_key) || [],
    [userData]
  );

  const permissionOptions = useMemo(() =>
    getVisiblePermissions(permissionsList, selectedRole, grantedPermissionCodes).map(permission => ({
      label: permission.permission_label,
      value: permission.permission_code
    })) || [], [permissionsList, selectedRole, grantedPermissionCodes]
  );

  const userDetails = getUserDetails();
  const userType = userDetails?.user_type;
  const isReadOnly = userType === 'support_admin';

  return (
    <Modal
      open={visible}
      title="Edit User"
      onCancel={onCancel}
      onOk={handleOk}
      destroyOnClose
      width={700}
      className="edit-user-modal"
      confirmLoading={isSubmitting}
      okText={isSubmitting ? "Saving..." : "Save Changes"}
      cancelButtonProps={{ disabled: isSubmitting }}
    >
      <Form
        layout="vertical"
        form={form}
        onFieldsChange={onFieldsChange}
        className="edit-user-modal__form"
      >
        <div className="edit-user-modal__form-row">
          <Form.Item
            className="edit-user-modal__form-item"
            label="First Name"
            name="user_firstname"
            rules={[
              {
                pattern: /^[a-zA-Z0-9\s.]+$/,
                message: "First name can only contain letters, numbers, spaces, and periods!",
              },
            ]}
          >
            <Input placeholder="Enter first name" disabled={isReadOnly} />
          </Form.Item>
          <Form.Item
            className="edit-user-modal__form-item"
            label="Last Name"
            name="user_lastname"
            rules={[
              {
                pattern: /^[a-zA-Z0-9\s.]*$/,
                message: "Last name can only contain letters, numbers, spaces, and periods!",
              },
            ]}
          >
            <Input placeholder="Enter last name" disabled={isReadOnly} />
          </Form.Item>
          <Form.Item
            className="edit-user-modal__form-item"
            label="Username"
            name="username"
            rules={[
              ...(MAX_USERNAME_LENGTH > 0
                ? [{
                  max: MAX_USERNAME_LENGTH,
                  message: `Username cannot exceed ${MAX_USERNAME_LENGTH} characters!`,
                }]
                : []),
              {
                pattern: /^[a-zA-Z0-9_.]+$/,
                message: "Username can only contain letters, numbers, underscores, and dots!",
              },
            ]}
          >
            <Input
              placeholder="Enter username"
              disabled={isReadOnly}
              maxLength={MAX_USERNAME_LENGTH || undefined}
              onKeyDown={warnOnUsernameLengthExceeded}
            />
          </Form.Item>
        </div>

        <div className="edit-user-modal__form-row">
          <Form.Item
            className="edit-user-modal__form-item"
            label="Create New Password"
            name="password"
          >
            <Input.Password placeholder="Enter new password (optional)" />
          </Form.Item>
          <Form.Item
            className="edit-user-modal__form-item"
            label="Confirm New Password"
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            className="edit-user-modal__form-item"
            label="Email"
            name="user_email"
            rules={[
              {
                type: "email",
                message: "Please enter a valid email address!",
              },
            ]}
          >
            <Input type="email" disabled={isReadOnly} placeholder="Enter email address" />
          </Form.Item>
        </div>

        <div className="edit-user-modal__form-row">
          <Form.Item
            className="edit-user-modal__form-item"
            label="Mobile"
            name="user_mobile"
            rules={[
              {
                pattern: /^[6-9]\d{9}$/,
                message: "Please enter a valid 10-digit mobile number starting with 6-9!",
              },
            ]}
          >
            <Input placeholder="Enter mobile number" disabled={isReadOnly} maxLength={10} />
          </Form.Item>
          <Form.Item
            className="edit-user-modal__form-item"
            label="Profile"
            name="user_type"
          >
            <Select placeholder="Select Role" disabled={isReadOnly} options={roleOptions} />
          </Form.Item>
          <WorkLocationField
            unitsList={unitsList}
            form={form}
            className="edit-user-modal__form-item"
            singleSelect={selectedRole === 'technician'}
          />
          {['radiologist', 'typist', 'hod'].includes(selectedRole || '') && (
            <PrimaryCenterField unitsList={unitsList} form={form} className="edit-user-modal__form-item" />
          )}
        </div>

        <div className="edit-user-modal__form-row">
          <Form.Item
            className="edit-user-modal__form-item"
            label="Designation"
            name="user_designation"
          >
            <Input placeholder="Enter designation" disabled={isReadOnly} />
          </Form.Item>
          <Form.Item
            className="edit-user-modal__form-item"
            label="Degree"
            name="user_degree"
          >
            <Input disabled={isReadOnly} placeholder="Enter degree" />
          </Form.Item>
          {['radiologist', 'typist', 'hod'].includes(selectedRole) && (
            <Form.Item
              className="edit-user-modal__form-item"
              label="Max Concurrent Reports"
              name="user_report_limit"
              tooltip="Maximum studies this user can open for reporting at the same time. Leave blank for unlimited."
            >
              <InputNumber
                min={1}
                step={1}
                precision={0}
                placeholder="Unlimited"
                style={{ width: '100%' }}
                disabled={isReadOnly}
              />
            </Form.Item>
          )}
          <Form.Item
            className="edit-user-modal__form-item"
            label="Modalities"
            name="user_modalities"
            rules={[
              {
                type: 'array',
                min: 1,
                required: true,
                message: 'Please select at least one modality!',
              },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select modalities"
              options={modalityOptions}
              allowClear
              disabled={isReadOnly}
            />
          </Form.Item>
        </div>

        {/* Assignment modality (technicians only). "Modalities" above is a
            visibility list; this single value decides which orders can be
            assigned to this technician. Empty = any modality. */}
        {selectedRole === 'technician' && (
          <div className="edit-user-modal__form-row">
            <Form.Item
              className="edit-user-modal__form-item"
              label="Assignment Modality"
              name="um_modality"
              tooltip="Orders of this modality can be assigned to this technician. Leave empty to allow all modalities."
            >
              <Select
                placeholder="Any modality"
                options={modalityOptions}
                allowClear
                showSearch
                optionFilterProp="label"
                disabled={isReadOnly}
              />
            </Form.Item>
          </div>
        )}

        <div className="edit-user-modal__form-row">
          <Form.Item
            className="edit-user-modal__form-item"
            name="consultant"
            valuePropName="checked"
          >
            <Checkbox disabled={isReadOnly}>Consultant</Checkbox>
          </Form.Item>
        </div>

        {/* Signature Section — only roles that sign reports need one */}
        {['hod', 'radiologist'].includes(selectedRole || '') && (
        <div className="edit-user-modal__form-row" style={{ width: '100%' }}>
          <Form.Item
            label="Signature (Required for HOD users)"
            style={{ width: '100%' }}
          >
            <div style={{ border: '1px solid #d9d9d9', borderRadius: '4px', padding: '8px' }}>
              <CustomEditor
                placeholder="Enter signature"
                initialContent={signatureHtml || ''}
                handleChange={handleSignatureChange}
                fromReporting={false}
              />
            </div>
          </Form.Item>
        </div>
        )}

        {/* Permissions Section */}
        <div className="edit-user-modal__form-row" style={{ width: '100%' }}>
          <Form.Item
            label="Permissions"
            name="permissions"
            style={{ width: '100%' }}
          >
            <Checkbox.Group disabled={isReadOnly} style={{ width: '100%' }} options={permissionOptions}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
                {/* Options are handled by the options prop above */}
              </div>
            </Checkbox.Group>
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default EditUser;
